import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { isInternalTraffic, applyInternalTrafficFromQuery } from '@/utils/internalTraffic';
import { trackGtagEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import {
  readPassPlanToken,
  savePassPlanToken,
  readPassPlanUrlToken,
  clearPassPlanUrlToken,
  downloadPassPlanPdf,
  type PassPlanPdfResult,
} from '@/utils/passPlanAccess';
import { redeemPassPlanAuth, type PassPlanRedeemResult } from '@/utils/passPlanAuth';
import { useAuth } from '@/contexts/AuthContext';
import PassPlanCapture from '@/components/PassPlanCapture';

/**
 * /pass-plan — the analytics-visible front door for the Pass Plan PDF.
 *
 * The workbook is now GATED: the raw file is not publicly reachable, so a valid
 * signed token is required. This page resolves a token from the URL (`?t=`, e.g.
 * the emailed link) or from a prior capture stored in localStorage. WITH one, it
 * fires `pass_plan_downloaded` (with UTMs) and streams the file from the
 * `/api/pass-plan` serverless route, which verifies the signature. WITHOUT one,
 * it renders the email-to-unlock form instead of handing the file out for free.
 *
 * A direct link to the file runs no JavaScript, so GA4 can't see a download;
 * routing through here keeps the funnel signal AND the gate. Email + site links
 * should point HERE (with a token), never at the raw file.
 *
 * The download must NOT navigate: when the tab left for the PDF, 9 of the 12
 * accounts this page minted (2026-09-09 to 11) never reached onboarding.
 */

const PassPlanDownload: React.FC = () => {
  const [params] = useSearchParams();
  const startedRef = useRef(false);
  // A token from the emailed link wins; otherwise fall back to one a prior capture
  // stored on this device. Null => no proof of capture, so show the unlock form.
  const [urlToken] = useState(() => readPassPlanUrlToken('pass-plan', params.toString()));
  const [token, setToken] = useState<string | null>(() => urlToken || readPassPlanToken());
  const [redeemed, setRedeemed] = useState<PassPlanRedeemResult | null>(null);
  const [pdf, setPdf] = useState<PassPlanPdfResult | null>(null);
  const download = (t: string) => {
    setPdf(null);
    void downloadPassPlanPdf(t).then(setPdf);
  };
  const { isAuthenticated, isLoadingUserData, userData } = useAuth();

  useEffect(() => {
    if (startedRef.current || !token) return;
    startedRef.current = true;
    // Persist a URL-provided token so a later visit (or a lost link) still unlocks.
    if (urlToken) { savePassPlanToken(token); clearPassPlanUrlToken('pass-plan'); }
    applyInternalTrafficFromQuery();
    if (!isInternalTraffic()) {
      trackGtagEvent(ANALYTICS_EVENTS.PASS_PLAN_DOWNLOADED, {
        utm_source: params.get('utm_source') ?? undefined,
        utm_medium: params.get('utm_medium') ?? undefined,
        utm_campaign: params.get('utm_campaign') ?? undefined,
        // Internal links carry an inert `src` instead of utm_*, because
        // captureAttributionIfPresent() overwrites the stored 90-day acquisition
        // record on any utm_*. Without recording it here, those clicks would be
        // indistinguishable from direct — the funnel signal would just vanish.
        internal_src: params.get('src') ?? undefined,
      });
    }
    download(token);

    // Opening this link IS the inbox proof (the token only ever reached one
    // address), so redeem it for a verified Firebase user. Never throws.
    // Nothing unloads the tab before it settles (in-app opens the PDF on a tap).
    let cancelled = false;
    void redeemPassPlanAuth(token, urlToken ? 'pass_plan_link' : 'pass_plan_page').then((r) => {
      if (!cancelled) setRedeemed(r);
    });
    return () => { cancelled = true; };
  }, [params, token]);

  // No token: gate the file behind the same email capture the diagnostic uses. On
  // success it hands back a fresh token, which unlocks the download in place.
  if (!token) {
    return (
      <main className="min-h-screen bg-[#FAF6EF] flex items-center justify-center px-4">
        <Helmet>
          <title>Unlock the 2026 ASWB Pass Plan (Free 15-Page Workbook) · Social Work Test Prep Academy</title>
          <meta name="description" content="Enter your email to unlock the free 15-page 2026 ASWB Pass Plan workbook: the format change explained, the FIRST/NEXT/BEST method, an 8-week study plan, and the Ethics traps." />
          <link rel="canonical" href="https://www.socialworktestprepacademy.com/pass-plan" />
          <meta property="og:title" content="The 2026 ASWB Pass Plan: free 15-page workbook" />
          <meta property="og:description" content="The new exam explained, the FIRST/NEXT/BEST method, an 8-week plan, and the Ethics traps. Just your email to unlock." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.socialworktestprepacademy.com/pass-plan" />
          <meta property="og:image" content="https://www.socialworktestprepacademy.com/images/pass-plan-og-image.png" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:image" content="https://www.socialworktestprepacademy.com/images/pass-plan-og-image.png" />
        </Helmet>
        <div className="max-w-lg w-full bg-white border-2 border-neutral-900 rounded-2xl p-8" style={{ boxShadow: '6px 6px 0 #2A2724' }}>
          <div className="text-4xl mb-3 text-center">📘</div>
          <h1 className="font-display font-bold text-2xl text-neutral-900 mb-2 text-center">Unlock your Pass Plan</h1>
          <p className="text-neutral-600 mb-5 text-center">
            The 2026 ASWB Pass Plan is a 15-page workbook: the new exam explained, the
            FIRST/NEXT/BEST method with worked questions, an 8-week plan, and the Ethics
            traps that catch retakers.
          </p>
          <PassPlanCapture
            lead={{ score: 0, total: 6, readiness: 0, weakAreas: [], examType: 'unknown' }}
            onCaptured={(fresh) => { if (fresh) { savePassPlanToken(fresh); setToken(fresh); } }}
            source="pass_plan_page"
          />
          <p className="text-sm text-neutral-500 mt-6 text-center">
            Rather find your weakest area first? The{' '}
            <Link to="/try?src=pass_plan" className="font-bold text-primary-700 underline">free 3-minute diagnostic</Link>{' '}
            answers that and unlocks the same workbook.
          </p>
          <p className="text-sm text-neutral-500 mt-3 text-center">
            Not sure what your state requires first?{' '}
            <Link to="/licensing" className="font-bold text-primary-700 underline">See your state.</Link>
          </p>
        </div>
      </main>
    );
  }

  // Wait for the user record too: an already-onboarded returner would otherwise
  // flash "Start your plan" before flipping to "Continue".
  const resolved = redeemed !== null && !isLoadingUserData;
  const signedIn = resolved && (redeemed?.outcome === 'signed-in' || isAuthenticated);
  const onboarded = !!userData?.profile?.onboardingCompleted;
  const trackStart = () => {
    if (isInternalTraffic()) return;
    trackGtagEvent(ANALYTICS_EVENTS.PASS_PLAN_START_CLICKED, {
      destination: onboarded ? '/practice' : '/onboarding',
      created: redeemed?.created === true,
    });
  };

  return (
    <main className="min-h-screen bg-[#FAF6EF] flex items-center justify-center px-4">
      <Helmet>
        <title>The 2026 ASWB Pass Plan (Free 15-Page Workbook) · Social Work Test Prep Academy</title>
        <meta name="description" content="Free 15-page workbook: the 2026 ASWB format change explained, the FIRST/NEXT/BEST method, an 8-week study plan, and the Ethics traps that catch retakers." />
        <link rel="canonical" href="https://www.socialworktestprepacademy.com/pass-plan" />
        <meta property="og:title" content="The 2026 ASWB Pass Plan: free 15-page workbook" />
        <meta property="og:description" content="The new exam explained, the FIRST/NEXT/BEST method, an 8-week plan, and the Ethics traps." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socialworktestprepacademy.com/pass-plan" />
        <meta property="og:image" content="https://www.socialworktestprepacademy.com/images/pass-plan-og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.socialworktestprepacademy.com/images/pass-plan-og-image.png" />
      </Helmet>
      <div className="max-w-lg w-full bg-white border-2 border-neutral-900 rounded-2xl p-8 text-center" style={{ boxShadow: '6px 6px 0 #2A2724' }}>
        <div className="text-4xl mb-3">📘</div>
        {typeof pdf === 'object' && pdf !== null ? (
          <>
            <h1 className="font-display font-bold text-2xl text-neutral-900 mb-2">Your Pass Plan is ready</h1>
            <button
              type="button"
              disabled={redeemed === null}
              onClick={() => window.location.assign(pdf.openUrl)}
              className="inline-block mb-5 px-6 py-3 rounded-xl bg-primary-600 text-white font-bold disabled:opacity-60"
              style={{ boxShadow: '0 4px 0 rgba(17,17,17,0.18)' }}
            >
              Open your Pass Plan
            </button>
          </>
        ) : pdf === 'failed' ? (
          <>
            <h1 className="font-display font-bold text-2xl text-neutral-900 mb-2">Your Pass Plan didn&apos;t download</h1>
            <p className="text-neutral-600 mb-5" data-clarity-mask="true">
              Something went wrong on our end.{' '}
              <button type="button" className="font-bold text-primary-700 underline" onClick={() => download(token)}>Try again</button>.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-display font-bold text-2xl text-neutral-900 mb-2">Your Pass Plan is downloading</h1>
            <p className="text-neutral-600 mb-5" data-clarity-mask="true">
              If it didn&apos;t start, <button type="button" className="font-bold text-primary-700 underline" onClick={() => download(token)}>download it here</button>.
              Read Chapters 1–3 today. They change how you see every practice question.
            </p>
          </>
        )}
        {signedIn ? (
          <>
            <p className="text-neutral-600 mb-5">
              You are signed in, so there is nothing to set up. Everything you have told us so far is
              saved to your account. The plan starts with your first question.
            </p>
            <Link
              to={onboarded ? '/practice' : '/onboarding'}
              onClick={trackStart}
              className="inline-block px-6 py-3 rounded-xl bg-primary-600 text-white font-bold"
              style={{ boxShadow: '0 4px 0 rgba(17,17,17,0.18)' }}
            >
              {onboarded ? 'Continue' : 'Start your plan'}
            </Link>
          </>
        ) : resolved ? (
          <>
            <p className="text-sm text-neutral-500 mb-6">Chapter 4 will ask where you stand. The free diagnostic answers that in three minutes.</p>
            <Link
              to="/try?src=pass_plan"
              className="inline-block px-6 py-3 rounded-xl bg-primary-600 text-white font-bold"
              style={{ boxShadow: '0 4px 0 rgba(17,17,17,0.18)' }}
            >
              Take the free diagnostic →
            </Link>
          </>
        ) : null}
        <p className="text-sm text-neutral-500 mt-4">
          Not sure what your state requires first?{' '}
          <Link to="/licensing" className="font-bold text-primary-700 underline">See your state.</Link>
        </p>
      </div>
    </main>
  );
};

export default PassPlanDownload;
